import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Mail, MapPin, Phone } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { contactApi, formatApiErrorDetail } from '../utils/api';
import { toast } from 'sonner';

export const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const { data } = await contactApi.submit(formData);
      
      if (data.success) {
        toast.success(data.message);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
      }
    } catch (error) {
      const errorMsg = formatApiErrorDetail(error.response?.data?.detail) || 'Something went wrong. Please try again.';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" ref={ref} className="py-32 bg-gradient-to-b from-[#0f0f10] via-[#1a1a1a] to-[#0f0f10] relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-[#8B1538] rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Number */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={isInView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-[#D4AF37]/20 text-8xl md:text-9xl font-bold mb-8"
        >
          08
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Let's Build Quality <span className="text-[#D4AF37]">Software Together</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Looking for a reliable QA partner? Our experts are ready to help you improve software quality.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 md:p-12 rounded-sm border border-[#D4AF37]/20"
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="contact-name-input"
                  className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37] transition-colors duration-300"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="contact-email-input"
                  className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37] transition-colors duration-300"
                  placeholder="john@example.com"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-300 mb-2">
                    Company
                  </label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37] transition-colors duration-300"
                    placeholder="Your Company"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                    Phone
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37] transition-colors duration-300"
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Message *
                </label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="contact-message-input"
                  className="bg-[#0f0f10] border-[#D4AF37]/20 text-white focus:border-[#D4AF37] transition-colors duration-300 resize-none"
                  placeholder="Tell us about your testing needs..."
                />
              </div>

              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit-button"
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4E4B0] text-[#1a1a1a] hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all duration-300 py-6 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </motion.div>
            </form>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="space-y-8"
          >
            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 rounded-sm border border-[#D4AF37]/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-[#1a1a1a]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Email Us</h3>
                  <p className="text-gray-400">support@axiumsolution.net</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 rounded-sm border border-[#D4AF37]/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-[#1a1a1a]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Support Hours</h3>
                  <p className="text-gray-400">Available 24/7 for enterprise clients</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 rounded-sm border border-[#D4AF37]/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#1a1a1a]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Registered Address</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    INDOAXIUM SOFTWARE SERVICES (OPC) PRIVATE LIMITED<br />
                    Villa No. 173, Sark Two, Mokila,<br />
                    Shankarpally, Shankarpalli Rangareddy KV,<br />
                    Rangareddy, Hyderabad,<br />
                    Telangana – 501203, India
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2d1f1f] p-8 rounded-sm border border-[#D4AF37]/20">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#F4E4B0] rounded-sm flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-[#1a1a1a]" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Operational Address</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">
                    S-1, D No. 402/3RT, Lane Number 14,<br />
                    Govt City Library, S.R Nagar,<br />
                    Hyderabad, Telangana – 500038, India
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};